import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { defaultMenu } from '../data/menu'
import { createExampleRequests } from '../data/seed'
import { findDuplicateRequest, validateSubmission, visibleRequestsForSession } from '../lib/requestPolicy'
import { archiveApiMenuItem, createApiMenuItem, fetchPublicMenu, fetchRequestStatus, fetchStaffBootstrap, patchApiMenuItem, patchApiSettings, patchApiStatus, submitApiRequest } from '../lib/api'
import type { AppSettings, EnvironmentMode, MenuItem, MenuItemDraft, RequestStatus, SalonRequest, SubmitRequestInput } from '../types'

const STORAGE_KEY = 'spellbound-stage1-state-v1'
const SESSION_KEY = 'spellbound-client-session-v1'
const TRACKED_REQUESTS_KEY = 'spellbound-tracked-request-ids-v1'
export const IS_API_MODE = import.meta.env.PROD || import.meta.env.VITE_DATA_MODE === 'api'

interface StoreState {
  menu: MenuItem[]
  requests: SalonRequest[]
  settings: AppSettings
  environmentMode: EnvironmentMode
}

interface StoreContextValue extends StoreState {
  sessionId: string
  submitRequest: (input: Omit<SubmitRequestInput, 'sessionId'>) => Promise<SalonRequest>
  updateStatus: (id: string, status: RequestStatus) => void
  createMenuItem: (item: MenuItemDraft) => Promise<MenuItem>
  updateMenuItem: (id: string, patch: Partial<MenuItemDraft & Pick<MenuItem, 'archived'>>) => Promise<MenuItem>
  archiveMenuItem: (id: string) => Promise<MenuItem>
  updateSettings: (patch: Partial<AppSettings>) => void
  clientRequests: SalonRequest[]
  resetDemo: () => void
}

const defaultSettings: AppSettings = {
  pauseNonessential: false,
  retentionDays: 30,
  displayName: 'Spellbound',
}

const sortMenu = (menu: MenuItem[]) => [...menu].sort((left, right) => left.sortOrder - right.sortOrder || left.name.localeCompare(right.name))

const normalizeMenu = (menu: MenuItem[]) => sortMenu(menu.map((item, index) => ({
  ...item,
  sortOrder: Number.isInteger(item.sortOrder) ? item.sortOrder : (index + 1) * 10,
  archived: item.archived === true,
})))

const initialState = (): StoreState => {
  if (IS_API_MODE) return { menu: defaultMenu, requests: [], settings: defaultSettings, environmentMode: 'prelaunch' }
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored) as Partial<StoreState>
      return {
        menu: normalizeMenu(parsed.menu ?? defaultMenu),
        requests: parsed.requests ?? createExampleRequests(),
        settings: { ...defaultSettings, ...parsed.settings },
        environmentMode: 'mock',
      }
    }
  } catch {
    // Local storage may be unavailable in privacy-restricted browsers.
  }
  return { menu: defaultMenu, requests: createExampleRequests(), settings: defaultSettings, environmentMode: 'mock' }
}

const getSessionId = () => {
  try {
    const existing = sessionStorage.getItem(SESSION_KEY)
    if (existing) return existing
    const created = crypto.randomUUID()
    sessionStorage.setItem(SESSION_KEY, created)
    return created
  } catch {
    return crypto.randomUUID()
  }
}

const StoreContext = createContext<StoreContextValue | null>(null)

export function MockAppStore({ children, route }: { children: ReactNode; route: 'client' | 'staff' }) {
  const [state, setState] = useState<StoreState>(initialState)
  const stateRef = useRef(state)
  const [sessionId] = useState(getSessionId)

  const commit = useCallback((updater: (current: StoreState) => StoreState) => {
    setState((current) => {
      const next = updater(current)
      try {
        if (!IS_API_MODE) localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
      } catch {
        // The app remains usable in-memory.
      }
      stateRef.current = next
      return next
    })
  }, [])

  useEffect(() => {
    if (!IS_API_MODE) return
    let active = true
    const load = async () => {
      try {
        if (route === 'staff') {
          const response = await fetchStaffBootstrap()
          if (!active) return
          const next = {
            menu: normalizeMenu(response.menu),
            requests: response.requests.map((request) => ({ ...request, sessionId: 'staff', idempotencyKey: '' })),
            settings: response.settings,
            environmentMode: response.environment.mode,
          }
          stateRef.current = next
          setState(next)
          return
        }
        const response = await fetchPublicMenu()
        if (!active) return
        commit((current) => ({ ...current, menu: normalizeMenu(response.menu), settings: { ...current.settings, ...response.settings } }))
        const tracked = JSON.parse(sessionStorage.getItem(TRACKED_REQUESTS_KEY) || '[]') as string[]
        const statuses = await Promise.all(tracked.slice(0, 20).map((id) => fetchRequestStatus(id).catch(() => null)))
        if (!active) return
        commit((current) => ({
          ...current,
          requests: statuses.filter((entry): entry is NonNullable<typeof entry> => Boolean(entry)).map(({ request }) => ({ ...request, sessionId, idempotencyKey: '' })),
        }))
      } catch (error) {
        console.error('Unable to load concierge data', error)
      }
    }
    void load()
    const timer = route === 'client' ? window.setInterval(load, 8_000) : undefined
    return () => { active = false; if (timer) window.clearInterval(timer) }
  }, [commit, route, sessionId])

  const submitRequest = useCallback(
    async (input: Omit<SubmitRequestInput, 'sessionId'>) => {
      if (IS_API_MODE) {
        const response = await submitApiRequest({ ...input, sessionId })
        const result: SalonRequest = { ...response.request, sessionId, idempotencyKey: input.idempotencyKey }
        commit((current) => ({ ...current, requests: [result, ...current.requests.filter((request) => request.id !== result.id)] }))
        const tracked = JSON.parse(sessionStorage.getItem(TRACKED_REQUESTS_KEY) || '[]') as string[]
        sessionStorage.setItem(TRACKED_REQUESTS_KEY, JSON.stringify([result.id, ...tracked.filter((id) => id !== result.id)].slice(0, 20)))
        return result
      }
      const current = stateRef.current
      const duplicate = findDuplicateRequest(current.requests, sessionId, input.idempotencyKey)
      if (duplicate) return duplicate
      const { item, notes } = validateSubmission(
        { ...input, sessionId },
        current.menu,
        current.requests,
        current.settings.pauseNonessential,
      )
      const now = new Date().toISOString()
      const result: SalonRequest = {
        id: `req_${crypto.randomUUID()}`,
        trackingCode: `LUNA-${Math.floor(1000 + Math.random() * 9000)}`,
        sessionId,
        idempotencyKey: input.idempotencyKey,
        menuItemId: item.id,
        itemName: item.name,
        category: item.category,
        priceCents: item.priceCents,
        location: input.location,
        notes,
        createdAt: now,
        updatedAt: now,
        status: 'pending',
        notifications: {
          email: 'sent',
        },
      }
      commit((latest) => {
        const alreadySaved = latest.requests.some(
          (request) => request.sessionId === sessionId && request.idempotencyKey === input.idempotencyKey,
        )
        return alreadySaved ? latest : { ...latest, requests: [result, ...latest.requests] }
      })
      await new Promise((resolve) => window.setTimeout(resolve, 420))
      return result
    },
    [commit, sessionId],
  )

  const updateStatus = useCallback(
    (id: string, status: RequestStatus) => {
      const previous = stateRef.current.requests.find((request) => request.id === id)?.status
      commit((current) => ({
        ...current,
        requests: current.requests.map((request) =>
          request.id === id ? { ...request, status, updatedAt: new Date().toISOString() } : request,
        ),
      }))
      if (IS_API_MODE) void patchApiStatus(id, status).catch(() => {
        if (previous) commit((current) => ({ ...current, requests: current.requests.map((request) => request.id === id ? { ...request, status: previous } : request) }))
      })
    },
    [commit],
  )

  const createMenuItem = useCallback(
    async (draft: MenuItemDraft) => {
      if (IS_API_MODE) {
        const { item } = await createApiMenuItem(draft)
        commit((current) => ({ ...current, menu: normalizeMenu([...current.menu, item]) }))
        return item
      }
      const item: MenuItem = { ...draft, id: `item_${crypto.randomUUID()}`, archived: false }
      commit((current) => ({ ...current, menu: normalizeMenu([...current.menu, item]) }))
      return item
    },
    [commit],
  )

  const updateMenuItem = useCallback(
    async (id: string, patch: Partial<MenuItemDraft & Pick<MenuItem, 'archived'>>) => {
      const previous = stateRef.current.menu.find((item) => item.id === id)
      if (!previous) throw new Error('Menu item not found.')
      const optimistic = { ...previous, ...patch }
      commit((current) => ({
        ...current,
        menu: normalizeMenu(current.menu.map((item) => (item.id === id ? optimistic : item))),
      }))
      if (!IS_API_MODE) return optimistic
      try {
        const { item } = await patchApiMenuItem(id, patch)
        commit((current) => ({ ...current, menu: normalizeMenu(current.menu.map((entry) => entry.id === id ? item : entry)) }))
        return item
      } catch (error) {
        commit((current) => ({ ...current, menu: normalizeMenu(current.menu.map((item) => item.id === id ? previous : item)) }))
        throw error
      }
    },
    [commit],
  )

  const archiveMenuItem = useCallback(
    async (id: string) => {
      const previous = stateRef.current.menu.find((item) => item.id === id)
      if (!previous) throw new Error('Menu item not found.')
      const archived = { ...previous, archived: true, enabled: false }
      commit((current) => ({ ...current, menu: normalizeMenu(current.menu.map((item) => item.id === id ? archived : item)) }))
      if (!IS_API_MODE) return archived
      try {
        const { item } = await archiveApiMenuItem(id)
        commit((current) => ({ ...current, menu: normalizeMenu(current.menu.map((entry) => entry.id === id ? item : entry)) }))
        return item
      } catch (error) {
        commit((current) => ({ ...current, menu: normalizeMenu(current.menu.map((item) => item.id === id ? previous : item)) }))
        throw error
      }
    },
    [commit],
  )

  const updateSettings = useCallback(
    (patch: Partial<AppSettings>) => {
      const previous = stateRef.current.settings
      commit((current) => ({ ...current, settings: { ...current.settings, ...patch } }))
      if (IS_API_MODE) void patchApiSettings(patch).catch(() => commit((current) => ({ ...current, settings: previous })))
    },
    [commit],
  )

  const resetDemo = useCallback(() => {
    if (IS_API_MODE) return
    const fresh = { menu: defaultMenu, requests: createExampleRequests(), settings: defaultSettings, environmentMode: 'mock' as const }
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(fresh))
    } catch {
      // Keep the in-memory reset.
    }
    setState(fresh)
    stateRef.current = fresh
  }, [])

  const value = useMemo(
    () => ({
      ...state,
      sessionId,
      submitRequest,
      updateStatus,
      createMenuItem,
      updateMenuItem,
      archiveMenuItem,
      updateSettings,
      clientRequests: visibleRequestsForSession(state.requests, sessionId),
      resetDemo,
    }),
    [state, sessionId, submitRequest, updateStatus, createMenuItem, updateMenuItem, archiveMenuItem, updateSettings, resetDemo],
  )

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>
}

export function useAppStore() {
  const value = useContext(StoreContext)
  if (!value) throw new Error('useAppStore must be used within MockAppStore')
  return value
}
